import React, { useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

const COURIERS = [
  { id: "pedidosya", label: "PedidosYa" },
  { id: "uber", label: "Uber" },
  { id: "vimenpaq", label: "Vimenpaq" },
  { id: "caribepack", label: "Caribe Pack" },
  { id: "mock", label: "Mock" },
];

const ALL_STATES = [
  "pending",
  "label_created",
  "picked_up",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "failed",
  "returned",
  "cancelled",
] as const;

type LogEntry = { ts: string; status: string; ok: boolean; message?: string };

function genTracking(prefix = "TRK") {
  const rnd = Math.random().toString(36).slice(2, 8).toUpperCase();
  const ts = Date.now().toString().slice(-6);
  return `${prefix}-${ts}-${rnd}`;
}

interface Props { lang: string }

export function ShippingTestPanel({ lang }: Props) {
  const es = lang === "es";
  const [courier, setCourier] = useState<string>("mock");
  const [tracking, setTracking] = useState<string>(() => genTracking());
  const [orderId, setOrderId] = useState<string>("");
  const [shipmentId, setShipmentId] = useState<string>("");
  const [location, setLocation] = useState<string>("Santo Domingo, DN");
  const [delayMs, setDelayMs] = useState<number>(800);
  const [sequence, setSequence] = useState<string[]>([
    "label_created", "picked_up", "in_transit", "out_for_delivery", "delivered",
  ]);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [singleStatus, setSingleStatus] = useState<string>("in_transit");

  const addLog = useCallback((e: LogEntry) => setLogs(p => [e, ...p].slice(0, 100)), []);

  const sendEvent = useCallback(async (status: string) => {
    const { data, error } = await supabase.functions.invoke("shipping-test", {
      body: {
        courier,
        tracking_number: tracking,
        status,
        description: `${courier} → ${status}`,
        location,
        order_id: orderId || undefined,
        shipment_id: shipmentId || undefined,
      },
    });
    if (error) {
      addLog({ ts: new Date().toLocaleTimeString(), status, ok: false, message: error.message });
      return false;
    }
    addLog({ ts: new Date().toLocaleTimeString(), status, ok: true, message: (data as any)?.shipment_id });
    if ((data as any)?.shipment_id && !shipmentId) setShipmentId((data as any).shipment_id);
    return true;
  }, [courier, tracking, location, orderId, shipmentId, addLog]);

  const runSequence = useCallback(async () => {
    if (!tracking) { alert(es ? "Tracking requerido" : "Tracking required"); return; }
    if (!orderId && !shipmentId) {
      alert(es ? "Necesitas order_id o shipment_id" : "Need order_id or shipment_id");
      return;
    }
    setRunning(true);
    for (const s of sequence) {
      const ok = await sendEvent(s);
      if (!ok) break;
      if (delayMs > 0) await new Promise(r => setTimeout(r, delayMs));
    }
    setRunning(false);
  }, [sequence, sendEvent, delayMs, tracking, orderId, shipmentId, es]);

  const toggleState = (s: string) => {
    setSequence(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  };
  const moveState = (idx: number, dir: -1 | 1) => {
    setSequence(p => {
      const n = [...p];
      const j = idx + dir;
      if (j < 0 || j >= n.length) return p;
      [n[idx], n[j]] = [n[j], n[idx]];
      return n;
    });
  };

  const presets = useMemo(() => ([
    { id: "happy", label: es ? "Feliz (entregado)" : "Happy (delivered)", seq: ["label_created","picked_up","in_transit","out_for_delivery","delivered"] },
    { id: "fail",  label: es ? "Falla en entrega" : "Delivery failure", seq: ["label_created","picked_up","in_transit","out_for_delivery","failed"] },
    { id: "ret",   label: es ? "Devuelto" : "Returned", seq: ["label_created","picked_up","in_transit","returned"] },
    { id: "cxl",   label: es ? "Cancelado temprano" : "Early cancel", seq: ["label_created","cancelled"] },
    { id: "full",  label: es ? "Todos los estados" : "All states", seq: [...ALL_STATES] as string[] },
  ]), [es]);

  const fieldStyle: React.CSSProperties = { width: "100%", fontSize: 12, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)" };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: "var(--tx3)", display: "block", marginBottom: 4, fontWeight: 600 };
  const cardStyle: React.CSSProperties = { background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: 10, padding: 14, marginBottom: 14 };

  return (
    <div style={{ padding: "10px 0" }}>
      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>{es ? "Configuración del envío" : "Shipment setup"}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <div>
            <label style={labelStyle}>Courier</label>
            <select style={fieldStyle} value={courier} onChange={e => setCourier(e.target.value)}>
              {COURIERS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Tracking</label>
            <div style={{ display: "flex", gap: 4 }}>
              <input style={fieldStyle} value={tracking} onChange={e => setTracking(e.target.value)} placeholder="TRK-..." />
              <button className="btn bo bs" style={{ fontSize: 10, padding: "0 8px" }} onClick={() => setTracking(genTracking())}>↻</button>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Order ID</label>
            <input style={fieldStyle} value={orderId} onChange={e => setOrderId(e.target.value)} placeholder="uuid" />
          </div>
          <div>
            <label style={labelStyle}>Shipment ID {es ? "(opcional)" : "(optional)"}</label>
            <input style={fieldStyle} value={shipmentId} onChange={e => setShipmentId(e.target.value)} placeholder="uuid" />
          </div>
          <div>
            <label style={labelStyle}>{es ? "Ubicación" : "Location"}</label>
            <input style={fieldStyle} value={location} onChange={e => setLocation(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>{es ? "Delay entre eventos (ms)" : "Delay between events (ms)"}</label>
            <input type="number" min={0} step={100} style={fieldStyle} value={delayMs} onChange={e => setDelayMs(Number(e.target.value) || 0)} />
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>{es ? "Disparar un solo evento" : "Trigger a single event"}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select style={{ ...fieldStyle, width: 200 }} value={singleStatus} onChange={e => setSingleStatus(e.target.value)}>
            {ALL_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="btn bp" disabled={running} onClick={() => sendEvent(singleStatus)}>
            {es ? "Enviar evento" : "Send event"}
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>{es ? "Secuencia de estados" : "State sequence"}</h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {presets.map(p => (
              <button key={p.id} className="btn bo bs" style={{ fontSize: 10 }} onClick={() => setSequence(p.seq)}>
                {p.label}
              </button>
            ))}
            <button className="btn bo bs" style={{ fontSize: 10 }} onClick={() => setSequence([])}>{es ? "Limpiar" : "Clear"}</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <div style={labelStyle}>{es ? "Estados disponibles" : "Available states"}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {ALL_STATES.map(s => (
                <label key={s} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={sequence.includes(s)} onChange={() => toggleState(s)} />
                  <code style={{ fontSize: 11 }}>{s}</code>
                </label>
              ))}
            </div>
          </div>
          <div>
            <div style={labelStyle}>{es ? "Orden de ejecución" : "Execution order"}</div>
            {sequence.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--tx3)", padding: 8 }}>
                {es ? "Selecciona estados o usa un preset" : "Pick states or use a preset"}
              </div>
            )}
            <ol style={{ paddingLeft: 18, margin: 0, display: "flex", flexDirection: "column", gap: 4 }}>
              {sequence.map((s, i) => (
                <li key={`${s}-${i}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <code style={{ flex: 1, fontSize: 11 }}>{s}</code>
                  <button className="btn bo bs" style={{ fontSize: 9, padding: "0 5px" }} onClick={() => moveState(i, -1)} disabled={i === 0}>↑</button>
                  <button className="btn bo bs" style={{ fontSize: 9, padding: "0 5px" }} onClick={() => moveState(i, 1)} disabled={i === sequence.length - 1}>↓</button>
                  <button className="btn bo bs" style={{ fontSize: 9, padding: "0 5px" }} onClick={() => toggleState(s)}>×</button>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button className="btn bp" disabled={running || sequence.length === 0} onClick={runSequence}>
            {running ? (es ? "Ejecutando…" : "Running…") : (es ? `Ejecutar secuencia (${sequence.length})` : `Run sequence (${sequence.length})`)}
          </button>
          <button className="btn bo" onClick={() => setLogs([])}>{es ? "Limpiar log" : "Clear log"}</button>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>{es ? "Log de eventos" : "Event log"}</h3>
        {logs.length === 0 ? (
          <div style={{ fontSize: 11, color: "var(--tx3)" }}>{es ? "Sin eventos aún" : "No events yet"}</div>
        ) : (
          <div style={{ maxHeight: 260, overflow: "auto", fontFamily: "monospace", fontSize: 11 }}>
            {logs.map((l, i) => (
              <div key={i} style={{ display: "flex", gap: 8, padding: "3px 0", borderBottom: "1px solid var(--bd2)" }}>
                <span style={{ color: "var(--tx3)" }}>{l.ts}</span>
                <span style={{ color: l.ok ? "#10b981" : "#ef4444", width: 18 }}>{l.ok ? "✓" : "✗"}</span>
                <span style={{ width: 140 }}>{l.status}</span>
                <span style={{ color: "var(--tx3)", flex: 1 }}>{l.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}